import {
  Avatar,
  Badge,
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllRewardShipments,
  getAllTenantsFilter,
  setModalFormOpen,
} from "redux/actions";
import styled from "styled-components";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import { IconCircle, IconInfo, IconPen } from "@aksara-ui/icons";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import { InputSearchbox } from "components/Form/InputSearchbox";
import RewardShipmentFormModal from "./components";
import moment from "moment";
import { RewardShipmentDetailsContent } from "./RewardShipmentDetails";
import { stringToColor } from "utilities/helper/stringToColor";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const RewardShipmentsContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading, isModalFormOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { rewardShipments } = useSelector(
    (state: any) => state.rewardShipmentReducer
  );
  const { currentPage, totalPage, limitPage, lengthPage, filterTenantPage } =
    useSelector((state: any) => state.rewardShipmentReducer);

  const [rewardShipmentId, setRewardShipmentId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedCatalogShipmentId, setSelectedCatalogShipmentId] =
    useState("");
  const [openCatalogShipmentDetailsModal, setOpenCatalogShipmentDetailsModal] =
    useState(false);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllRewardShipments(
        1,
        event.target.value,
        limitPage,
        filterTenantPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllRewardShipments(currentPage, "", limitPage, filterTenantPage) as any
    );
  };

  const handleOpenCatalogShipmentDetailsModal = (catalogShipmentId: string) => {
    setOpenCatalogShipmentDetailsModal(true);
    setSelectedCatalogShipmentId(catalogShipmentId);
  };

  const handleCloseCatalogShipmentDetailsModal = () => {
    setOpenCatalogShipmentDetailsModal(false);
    setSelectedCatalogShipmentId("");
  };

  const handleOpenFormModal = (rewardShipmentId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setRewardShipmentId(rewardShipmentId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setRewardShipmentId("");
  };

  useEffect(() => {
    dispatch(getAllTenantsFilter() as any);
    dispatch(getAllRewardShipments() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Reward Shipment
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isTablet ? "column" : "row"}
          style={{ gap: 5 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Date</TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Phone Number</TableHeadCell>
              <TableHeadCell>Customer Address</TableHeadCell>
              <TableHeadCell>Tracking Number</TableHeadCell>
              <TableHeadCell>Service</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={8} />
            ) : !rewardShipments.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={8} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              rewardShipments.map((reward: any) => {
                const getAddress = (user: any) => {
                  if (!user) return "";
                  const address = user.address || "";
                  const address2 = user.address_2 ? `, ${user.address_2}` : "";
                  return `${address}${address2}`;
                };

                const name = reward.user ? reward.user.name : "";
                const color = stringToColor(name);

                return (
                  <TableBodyRow key={reward.id}>
                    <TableBodyCell>
                      {moment(reward.created_at).format("DD MMMM YYYY")}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Avatar
                        name={name}
                        backgroundColor={color}
                        style={{ marginRight: 15 }}
                      />
                      {name}
                    </TableBodyCell>
                    <TableBodyCell>{reward.user.phone_number}</TableBodyCell>
                    <TableBodyCell>{getAddress(reward.user)}</TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      {reward.tracking_number ? (
                        <Box
                          onClick={() =>
                            window.open(reward.tracking_link ?? "", "_blank")
                          }
                        >
                          <Badge
                            size="lg"
                            variant={"info"}
                            style={{
                              paddingLeft: 20,
                              paddingRight: 20,
                              cursor: "pointer",
                            }}
                          >
                            {reward.tracking_number}
                          </Badge>
                        </Box>
                      ) : (
                        ""
                      )}
                    </TableBodyCell>
                    <TableBodyCell color={theme.colors.grey08}>
                      {reward.service ? reward.service : ""}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge
                        size="md"
                        variant={
                          reward.status === "pending"
                            ? "warning"
                            : reward.status === "in_transit"
                            ? "default"
                            : reward.status === "delivered"
                            ? "success"
                            : "critical"
                        }
                        style={{
                          color:
                            reward.status === "pending"
                              ? "#F4B405"
                              : reward.status === "in_transit"
                              ? "#006FE6"
                              : reward.status === "delivered"
                              ? "#0C965A"
                              : "#FF6150",
                        }}
                      >
                        <IconCircle
                          size={10}
                          fill={
                            reward.status === "pending"
                              ? "#F4B405"
                              : reward.status === "in_transit"
                              ? "#006FE6"
                              : reward.status === "delivered"
                              ? "#0C965A"
                              : "#FF6150"
                          }
                          style={{ marginRight: 5 }}
                        />
                        {reward.status === "in_transit"
                          ? "In Transit"
                          : toCapitilizeCase(reward.status)}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <IconButton
                        onClick={() =>
                          handleOpenCatalogShipmentDetailsModal(reward.id)
                        }
                      >
                        <IconInfo fill={theme.colors.blue05} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenFormModal(reward.id)}
                      >
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllRewardShipments(
                select,
                searchValue,
                limitPage,
                filterTenantPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllRewardShipments(
                  1,
                  searchValue,
                  selectedItem,
                  filterTenantPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <RewardShipmentFormModal
        rewardShipmentId={rewardShipmentId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <RewardShipmentDetailsContent
        catalogShipmentId={selectedCatalogShipmentId}
        isOpen={openCatalogShipmentDetailsModal}
        onClose={() => handleCloseCatalogShipmentDetailsModal()}
      />
    </Box>
  );
};

export default RewardShipmentsContent;
